import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Spin } from 'antd';
import { parseQueryString } from '../utils/helpers';
import { fetchOrderInfo } from '../services/api';

const SuccessPage = () => {
  const [paymentInfo, setPaymentInfo] = useState({
    amount: 0,
    orderId: '',
    timestamp: new Date().toLocaleString(),
    type: 'order',
    coins: 0,
    unlocked: null
  });
  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);
  const [isOnlyChargeOrder, setIsOnlyChargeOrder] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const queryParams = parseQueryString(location.search);
    
    if (queryParams.orderId) {
      const orderId = queryParams.orderId;
      const amount = queryParams.amount ? parseFloat(queryParams.amount) : 0;
      const paidAt = queryParams.paidAt ? decodeURIComponent(queryParams.paidAt) : new Date().toISOString();
      const type = queryParams.type || 'order';
      const unlocked = queryParams.unlocked || null;
      // 判断订单号是否以charge开头
      const isOnlyCharge = orderId.startsWith('charge');
      setIsOnlyChargeOrder(isOnlyCharge);

      // 根据充值金额计算能量币（仅用于显示）
      const rechargeOptions = [
        { value: 39.9, coins: 4600, bonus: '15%' },
        { value: 29.9, coins: 3360, bonus: '12%' },
        { value: 19.9, coins: 2200, bonus: '10%' },
        { value: 9.9, coins: 1000, bonus: '' }
      ];
      
      let coins = 0;
      if (type === 'recharge') {
        const option = rechargeOptions.find(opt => opt.value === amount);
        coins = option ? option.coins : 0;
      }
      
      setPaymentInfo({
        amount,
        orderId,
        timestamp: new Date(paidAt).toLocaleString(),
        type,
        coins,
        unlocked
      });
      
      // Fetch complete order details if it's an order payment
      if (type === 'order' || unlocked) {
        fetchOrderDetails(unlocked || orderId);
      } else {
        setLoading(false);
      }
    } else {
      // If no payment info is found, redirect back to payment page
      navigate('/payment');
    }
  }, [location.search, navigate]);

  const fetchOrderDetails = async (orderId) => {
    try {
      setLoading(true);
      const response = await fetchOrderInfo(orderId);
      
      if (response.success) {
        setOrderDetails(response.data);
      } else {
        console.error('Failed to fetch order details');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClosePage = () => {
    // 尝试关闭当前页面/窗口
    if (window.opener || window.parent !== window) {
      // 如果是弹窗或iframe，关闭当前窗口
      window.close();
    } else {
      // 如果无法关闭，返回上一页
      window.history.back();
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        backgroundColor: '#f8f9fa'
      }}>
        <Spin size="large" tip="加载订单详情..." />
      </div>
    );
  }

  return (
    <div className="success-page" style={{
      padding: '20px 16px',
      maxWidth: '400px',
      margin: '0 auto',
      minHeight: '100vh',
      backgroundColor: '#f8f9fa',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
    }}>
      {/* 成功图标和标题 */}
      <div style={{
        textAlign: 'center',
        paddingTop: '80px',
        paddingBottom: '40px'
      }}>
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          backgroundColor: '#34c759',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 24px',
          boxShadow: '0 4px 20px rgba(52, 199, 89, 0.2)'
        }}>
          <span style={{
            fontSize: '36px',
            color: 'white'
          }}>✓</span>
        </div>
        
        <h1 style={{
          fontSize: '20px',
          fontWeight: '600',
          color: '#333',
          margin: '0 0 8px 0'
        }}>
          {paymentInfo.type === 'recharge' ? '充值成功' : '支付成功'}
        </h1>
        
        <p style={{
          fontSize: '14px',
          color: '#8e8e93',
          margin: '0'
        }}>
          {isOnlyChargeOrder ? 
            `已成功充值 ${paymentInfo.coins} 能量币` : 
            paymentInfo.type === 'recharge' ? 
            `已成功充值 ${paymentInfo.coins} 能量币并解锁帖子` : 
            '帖子已成功解锁'}
        </p>
      </div>

      {/* 支付详情卡片 */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '120px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)'
      }}>
        <h2 style={{
          fontSize: '16px',
          fontWeight: '600',
          color: '#333',
          margin: '0 0 20px 0'
        }}>
          支付详情
        </h2>
        
        <div className="payment-details">
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            paddingBottom: '16px',
            marginBottom: '16px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <span style={{ fontSize: '14px', color: '#666' }}>订单编号</span>
            <span style={{ fontSize: '14px', color: '#333', fontWeight: '500' }}>
              {paymentInfo.orderId}
            </span>
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            paddingBottom: '16px',
            marginBottom: '16px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <span style={{ fontSize: '14px', color: '#666' }}>支付金额</span>
            <div style={{ textAlign: 'right' }}>
              <span style={{ 
                fontSize: '20px', 
                color: '#333', 
                fontWeight: '600'
              }}>
                ¥{paymentInfo.amount.toFixed(paymentInfo.type === 'recharge' ? 1 : 2)}
              </span>
              {paymentInfo.type === 'recharge' && (
                <div style={{
                  fontSize: '12px',
                  color: '#ff69b4',
                  marginTop: '2px'
                }}>
                  ❤️
                </div>
              )}
            </div>
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            paddingBottom: '16px',
            marginBottom: '16px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <span style={{ fontSize: '14px', color: '#666' }}>支付时间</span>
            <span style={{ fontSize: '14px', color: '#333' }}>
              {paymentInfo.timestamp}
            </span>
          </div>
          
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center'
          }}>
            <span style={{ fontSize: '14px', color: '#666' }}>支付状态</span>
            <span style={{ 
              fontSize: '14px', 
              color: '#34c759',
              fontWeight: '500'
            }}>
              已完成
            </span>
          </div>
        </div>
      </div>

      {/* 底部按钮 */}
      <div style={{ 
        position: 'fixed',
        bottom: '0',
        left: '0',
        right: '0',
        padding: '16px',
        backgroundColor: '#fff',
        borderTop: '1px solid #e1e5e9'
      }}>
        <div style={{ maxWidth: '400px', margin: '0 auto' }}>
          <Button
            type="primary"
            size="large"
            block
            onClick={handleClosePage}
            style={{
              backgroundColor: '#8e7cc3',
              borderColor: '#8e7cc3',
              borderRadius: '24px',
              height: '48px',
              fontSize: '16px',
              fontWeight: '500',
              boxShadow: 'none',
              marginBottom: '12px'
            }}
          >
            关闭页面
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
